import { apiWorkchestraCall } from "@/hooks/services/workchestra/base";
import { ENDPOINT } from "@/hooks/services/workchestra/endpoints";
import { UserProfile } from "@/hooks/services/pps/types/user-profile.type";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { HttpMethod } from "@/types/https";
import { userManager } from "./oidc-config";

const BASE_URL_WORKCHESTRA = window.location.origin;
const OIDC_STORAGE_KEY = `oidc.user:${BASE_URL_WORKCHESTRA}:WorkchestraWeb`;
const AUTH_COOKIE_NAME = "idsrv.session";
const LOGIN_URL = `${BASE_URL_WORKCHESTRA}/Identity/Login`;

// Determine if we're running in standalone mode
const IS_STANDALONE = import.meta.env.VITE_STANDALONE_MODE === "true";

/**
 * Check if the user is authenticated
 * - In embedded mode: checks for auth cookie
 * - In standalone mode: checks OIDC user session
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    if (IS_STANDALONE) {
      // Standalone mode: check OIDC user
      const user = await userManager.getUser();
      return user !== null && !user.expired;
    } else {
      // Embedded mode: check auth cookie
      const cookies = document.cookie.split(";");
      return cookies.some((cookie) =>
        cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`),
      );
    }
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Synchronous version for embedded mode only
 * @deprecated Use isAuthenticated() instead
 */
export const isAuthenticatedSync = (): boolean => {
  try {
    const cookies = document.cookie.split(";");
    return cookies.some((cookie) =>
      cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`),
    );
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Redirect to login page or initiate OIDC login flow
 */
export const redirectToLogin = async (): Promise<void> => {
  if (IS_STANDALONE) {
    // Standalone mode: initiate OIDC login
    await userManager.signinRedirect();
  } else {
    // Embedded mode: redirect to Identity login page
    window.location.href = LOGIN_URL;
  }
};

/**
 * Login function for standalone mode
 */
export const login = async (): Promise<void> => {
  await userManager.signinRedirect();
};

/**
 * Logout function
 */
export const logout = async (): Promise<void> => {
  if (IS_STANDALONE) {
    await userManager.signoutRedirect();
  } else {
    window.location.href = `${BASE_URL_WORKCHESTRA}/Identity/Logout`;
  }
};

/**
 * Get access token
 * - In embedded mode: reads from sessionStorage
 * - In standalone mode: gets from OIDC user manager
 */
export const getAccessToken = async (): Promise<string | null> => {
  const { setToken } = useUserProfileStore.getState();
  try {
    if (IS_STANDALONE) {
      // Standalone mode: get from OIDC user manager
      const user = await userManager.getUser();
      const accessToken = user?.access_token || null;
      setToken(accessToken);
      return accessToken;
    } else {
      // Embedded mode: read from sessionStorage
      const oidcUser = sessionStorage.getItem(OIDC_STORAGE_KEY);
      if (oidcUser) {
        const user = JSON.parse(oidcUser);
        const accessToken = user?.access_token || null;
        setToken(accessToken);
        return accessToken;
      }
    }
  } catch (error) {
    console.error("Error retrieving access token:", error);
  }
  return null;
};

/**
 * Synchronous version for embedded mode only
 * @deprecated Use getAccessToken() instead
 */
export const getAccessTokenSync = (): string | null => {
  const { setToken } = useUserProfileStore.getState();
  try {
    const oidcUser = sessionStorage.getItem(OIDC_STORAGE_KEY);
    if (oidcUser) {
      const user = JSON.parse(oidcUser);
      setToken(user.access_token || null);

      return user.access_token || null;
    }
  } catch (error) {
    console.error("Error retrieving access token:", error);
  }
  return null;
};

/**
 * Handle OIDC callback after redirect
 * Only used in standalone mode
 */
export const handleCallback = async (): Promise<void> => {
  if (IS_STANDALONE) {
    await userManager.signinRedirectCallback();
  }
};

export const trySilentSignIn = async (): Promise<boolean> => {
  if (!IS_STANDALONE) {
    return false;
  }

  try {
    const user = await userManager.signinSilent();
    if (user && !user.expired) {
      sessionStorage.setItem("lastLoginTime", Date.now().toString());
      return true;
    }
    return false;
  } catch (error: any) {
    console.error("Error signing in silently:", error);
    return false;
  }
};

export const fetchUserProfile = async (): Promise<UserProfile> => {
  const token = await getAccessToken();

  if (!token) throw new Error("No access token available");
  return apiWorkchestraCall<UserProfile>({
    url: ENDPOINT.USER_PROFILE,
    method: HttpMethod.GET,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};
